import { useQuery } from "@tanstack/react-query";
import { AUDIT_ACTIONS, AUDIT_RESOURCE_TYPES, type AuditLog } from "@/contracts";

import { auditLogsQuery, type AuditLogFilters } from "@/api/queries";
import { useAuth } from "@/auth/auth-context";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardBody } from "@/components/ui/card";
import { SelectField } from "@/components/ui/field";
import { useTranslation } from "react-i18next";
import { ErrorBlock, LoadingBlock, PageHeader } from "@/components/ui/page";
import { Pagination } from "@/components/ui/pagination";
import { SearchInput } from "@/components/ui/search-input";
import { DataTable, EmptyRow, Td, Th } from "@/components/ui/table";
import { OrganizationFilter } from "@/features/organizations/organization-filter";
import { useApiClient } from "@/lib/api-context";
import { formatDateTime, formatRelative } from "@/lib/format";
import { useListFilters } from "@/lib/use-list-filters";
import {
  actionGroupEntries,
  actionLabel,
  actionTone,
  groupOf,
  metadataLabel,
  metadataValue,
} from "./audit-labels";

/**
 * Periods as a fixed list rather than two date pickers.
 *
 * A pair of datetime inputs asks the reader to think about time zones and
 * about which end is inclusive. Nobody auditing a change wants that; they
 * want "this week". The width travels to the query; the boundary is worked
 * out when the request goes out.
 */
const PERIODS = {
  "": { labelKey: "anyTime" as const, hours: null },
  "24h": { labelKey: "period24h" as const, hours: 24 },
  "7d": { labelKey: "period7d" as const, hours: 24 * 7 },
  "30d": { labelKey: "period30d" as const, hours: 24 * 30 },
} as const satisfies Record<string, { labelKey: "anyTime" | "period24h" | "period7d" | "period30d"; hours: number | null }>;

type PeriodKey = keyof typeof PERIODS;

function periodOf(value: string): PeriodKey {
  return value in PERIODS ? (value as PeriodKey) : "";
}

/** Actions grouped by their prefix, for the optgroups in the filter. */
function groupedActions() {
  return actionGroupEntries().map(({ group, label }) => ({
    label,
    actions: AUDIT_ACTIONS.filter((action) => groupOf(action) === group),
  }));
}

export function AuditPage() {
  const { t } = useTranslation("pages");
  const { t: tCommon } = useTranslation("common");
  const { user } = useAuth();
  const isSuperAdmin = user?.role === "SUPER_ADMIN";

  const { value: filterValue, page, setFilter, setPage } = useListFilters();

  const period = periodOf(filterValue("period"));
  const hours = PERIODS[period].hours;
  const actorUserId = filterValue("actorUserId");
  const resourceId = filterValue("resourceId");

  const filters: AuditLogFilters = {
    ...(filterValue("search") ? { search: filterValue("search") } : {}),
    ...(filterValue("action") ? { action: filterValue("action") } : {}),
    ...(filterValue("resourceType") ? { resourceType: filterValue("resourceType") } : {}),
    ...(actorUserId ? { actorUserId } : {}),
    ...(resourceId ? { resourceId } : {}),
    ...(filterValue("organizationId") ? { organizationId: filterValue("organizationId") } : {}),
    ...(hours === null ? {} : { hours }),
    page,
  };

  const client = useApiClient();
  const auditLogs = useQuery(auditLogsQuery(client, filters));

  return (
    <>
      <PageHeader
        title={t("auditTitle")}
        description={t("auditDescription")}
      />

      <Card className="mb-4">
        <CardBody>
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            <SearchInput
              value={filters.search ?? ""}
              placeholder={t("searchAudit")}
              onChange={(value) => setFilter("search", value)}
            />

            <SelectField
              label={tCommon("action")}
              value={filters.action ?? ""}
              onChange={(value) => setFilter("action", value)}
              data={[
                { value: "", label: tCommon("allActions") },
                ...groupedActions().map((group) => ({
                  group: group.label,
                  items: group.actions.map((action) => ({
                    value: action,
                    label: actionLabel(action),
                  })),
                })),
              ]}
            />

            <SelectField
              label={tCommon("resource")}
              value={filters.resourceType ?? ""}
              onChange={(value) => setFilter("resourceType", value)}
              data={[
                { value: "", label: tCommon("allResources") },
                ...AUDIT_RESOURCE_TYPES.map((resourceType) => ({
                  value: resourceType,
                  label: resourceType,
                })),
              ]}
            />

            <SelectField
              label={tCommon("period")}
              value={period}
              onChange={(value) => setFilter("period", value)}
              data={Object.entries(PERIODS).map(([key, entry]) => ({
                value: key,
                label: tCommon(entry.labelKey),
              }))}
            />

            {isSuperAdmin ? (
              <OrganizationFilter
                value={filters.organizationId ?? ""}
                onChange={(value) => setFilter("organizationId", value)}
              />
            ) : null}
          </div>

          {/*
            These two are set by clicking a cell, not by a control above, so
            without a way back out the reader is stranded in a filtered view
            wondering why the table is nearly empty.
          */}
          {actorUserId || resourceId ? (
            <div className="mt-3 flex flex-wrap items-center gap-2 border-t border-border-subtle pt-3">
              {actorUserId ? (
                <FilterChip label={t("onePerson")} onClear={() => setFilter("actorUserId", "")} />
              ) : null}
              {resourceId ? (
                <FilterChip label={t("oneResource")} onClear={() => setFilter("resourceId", "")} />
              ) : null}
            </div>
          ) : null}
        </CardBody>
      </Card>

      {auditLogs.isPending ? <LoadingBlock label={t("loadingAudit")} /> : null}

      {auditLogs.isError ? (
        <ErrorBlock error={auditLogs.error} onRetry={() => void auditLogs.refetch()} />
      ) : null}

      {auditLogs.data ? (
        <Card>
          <DataTable caption={t("auditTitle")}>
            <thead>
              <tr>
                <Th>{t("colWhen")}</Th>
                <Th>{t("colWho")}</Th>
                {isSuperAdmin ? <Th>{t("colOrganization")}</Th> : null}
                <Th>{t("colAction")}</Th>
                <Th>{t("colResource")}</Th>
                <Th>{t("colDetails")}</Th>
              </tr>
            </thead>
            <tbody>
              {auditLogs.data.data.length === 0 ? (
                <EmptyRow colSpan={isSuperAdmin ? 6 : 5}>{t("emptyAudit")}</EmptyRow>
              ) : (
                auditLogs.data.data.map((entry) => (
                  <AuditRow
                    key={entry.id}
                    entry={entry}
                    showOrganization={isSuperAdmin}
                    onFilterActor={() => setFilter("actorUserId", entry.actorUserId ?? "")}
                    onFilterResource={() => setFilter("resourceId", entry.resourceId ?? "")}
                  />
                ))
              )}
            </tbody>
            <tfoot>
              <tr>
                <Td colSpan={isSuperAdmin ? 6 : 5} className="border-b-0">
                  <Pagination
                    page={auditLogs.data.pagination.page}
                    totalPages={auditLogs.data.pagination.totalPages}
                    total={auditLogs.data.pagination.total}
                    noun={{ one: tCommon("noun.entry.one"), other: tCommon("noun.entry.other") }}
                    onChange={setPage}
                  />
                </Td>
              </tr>
            </tfoot>
          </DataTable>
        </Card>
      ) : null}
    </>
  );
}

function FilterChip({ label, onClear }: { label: string; onClear: () => void }) {
  const { t } = useTranslation("common");

  return (
    <span className="inline-flex items-center gap-2 rounded-full bg-brand-soft px-3 py-1 text-xs text-brand-strong">
      {t("filterChipShowing", { label })}
      <button
        type="button"
        onClick={onClear}
        className="font-semibold underline underline-offset-2"
      >
        {t("clear")}
      </button>
    </span>
  );
}

function AuditRow({
  entry,
  showOrganization,
  onFilterActor,
  onFilterResource,
}: {
  entry: AuditLog;
  showOrganization: boolean;
  onFilterActor: () => void;
  onFilterResource: () => void;
}) {
  return (
    <tr>
      <Td>
        <span title={formatDateTime(entry.createdAt)}>{formatRelative(entry.createdAt)}</span>
      </Td>
      <Td>
        {entry.actorUserId ? (
          <Button variant="ghost" size="sm" onClick={onFilterActor} className="-ml-2">
            {entry.actorName ?? entry.actorEmail ?? "Unknown"}
          </Button>
        ) : (
          // The actor was deleted, or there never was one. The email is kept
          // on the row precisely so the trail still names somebody.
          <span>{entry.actorEmail ?? "System"}</span>
        )}
        {entry.actorName && entry.actorEmail ? (
          <span className="block text-xs text-ink-muted">{entry.actorEmail}</span>
        ) : null}
      </Td>
      {showOrganization ? <Td>{entry.organizationName ?? "Platform"}</Td> : null}
      <Td>
        <Badge tone={actionTone(entry.action)}>{actionLabel(entry.action)}</Badge>
      </Td>
      <Td>
        {entry.resourceId ? (
          <Button variant="ghost" size="sm" onClick={onFilterResource} className="-ml-2">
            {entry.resourceType}
          </Button>
        ) : (
          <span>{entry.resourceType}</span>
        )}
      </Td>
      <Td>
        <Metadata metadata={entry.metadata} />
      </Td>
    </tr>
  );
}

function Metadata({ metadata }: { metadata: Record<string, unknown> | null }) {
  const pairs = Object.entries(metadata ?? {});

  if (pairs.length === 0) {
    return <span className="text-ink-muted">—</span>;
  }

  return (
    <dl className="flex flex-col gap-0.5 text-xs">
      {pairs.map(([key, value]) => (
        <div key={key} className="flex gap-1">
          <dt className="text-ink-muted">{metadataLabel(key)}:</dt>
          <dd>{metadataValue(value)}</dd>
        </div>
      ))}
    </dl>
  );
}
